// @flow
import React, { Fragment, useContext } from 'react';

import ratios from 'Components/utils/ratios/ratios.utils.js';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { truncate } from 'Components/utils/string/string.util';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { ProductIntroductionActions } from 'Components/widgets/product-introduction-actions/ProductIntroductionActions.jsx';
import { TextSeeMore } from 'Components/frameworks/text-see-more/TextSeeMore.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

type Props = {
  className?: string,
  communityLabel?: string,
  iconName: IconName,
  imageSrc?: string,
  productCountry?: string,
  productName?: string,
  ratingImage?: ?{
    alt: string,
    src: string,
  },
  reduceLabel?: string,
  seasonLink: ?{
    iconName?: IconName,
    label: string,
    url: string,
  },
  seeMoreLabel?: string,
  text?: string,
  title?: string,
};

export function ProductIntroduction(props: Props) {
  const {
    className,
    communityLabel,
    iconName,
    imageSrc,
    productCountry,
    productName,
    ratingImage,
    reduceLabel,
    seasonLink,
    seeMoreLabel,
    text,
    title,
  } = props;

  const { isDesktop, isTablet } = useContext(ResponsiveContext);

  if (!title) return null;

  const seeMoreSize = isDesktop ? 120 : 90;

  const top = (
    <ProductIntroductionActions
      className="mb-5"
      communityLabel={communityLabel}
      ratingImage={ratingImage}
      seasonLink={seasonLink}
    />
  );

  const logo = (
    <div className="sm:inline-block mb-8 sm:mb-5 text-blue text-center">
      <Icon alt={productName} isNotSquare name={iconName} width={isDesktop ? '7rem' : '5rem'} />
      {productName && <p className="reset-paragraph">{productName}</p>}
      {productCountry && <p className="reset-paragraph">{productCountry}</p>}
    </div>
  );

  const content = (
    <Fragment>
      <Title className="mb-5" theme="title-brand-2">
        {truncate(title, 50)}
      </Title>
      <TextSeeMore
        hasNoScrollOnCollapse
        isCollapseEnabled
        seeLessLabel={reduceLabel}
        seeMoreLabel={seeMoreLabel}
        seeMoreSize={seeMoreSize}
        text={text}
        theme="text-sans-3"
      />
    </Fragment>
  );

  return isTablet ? (
    <article className={className}>
      {top}
      <div className="flex">
        <div className="h-full relative rtl:pr-3 w-1/2 image-responsive-cell z-0">
          <Image
            imageUrl={imageSrc}
            className="rounded-tiny overflow-hidden"
            ratioKey={ratios.R_63_BY_58}
          />
          <div
            className="absolute bg-white rtl:right-0 z-1 rounded-tiny"
            style={{ width: '11%', height: '80%', top: '10%' }}
          />
        </div>
        <div className="w-1/2 px-3 lg:pt-5 lg:rtl:pr-24">
          {logo}
          {content}
        </div>
      </div>
    </article>
  ) : (
    <article className={className}>
      {logo}
      {top}
      {content}
    </article>
  );
}
